// Loading skeletons: the page keeps its shape (hero panel + lower blocks)
// with a slow cold shimmer instead of an empty screen.
import i18n from '@/i18n';
import type { CSSProperties } from 'react';
import { pageByPath } from '@/app/nav';
import { cx } from '@/lib/format';

const Sk = ({ w, h, r, className }: { w?: string; h: number | string; r?: number; className?: string }) => (
  <i className={cx('sk', className)} style={{ width: w ?? '100%', height: typeof h === 'number' ? `${h}px` : h, borderRadius: r } as CSSProperties} />
);

/** Lower layout per route: column spans and inner shapes. */
const LOWER: Record<string, Array<[number, 'list' | 'chart' | 'cards' | 'rows' | 'text']>> = {
  '/': [[6, 'chart'], [6, 'cards'], [5, 'rows'], [3, 'rows'], [4, 'text']],
  '/news': [[8, 'cards'], [4, 'rows']],
  '/calendar': [[7, 'rows'], [5, 'text'], [12, 'text']],
  '/wall-street': [[7, 'text'], [5, 'rows'], [7, 'rows'], [5, 'rows']],
  '/portfolio': [[7, 'chart'], [5, 'rows'], [7, 'rows'], [5, 'rows']],
  '/signals': [[12, 'chart'], [12, 'chart'], [7, 'rows'], [5, 'rows']],
  '/alerts': [[12, 'rows'], [7, 'rows'], [5, 'text']],
  '/whales': [[8, 'cards'], [4, 'text'], [12, 'rows']],
  '/social': [[7, 'cards'], [5, 'rows']],
  '/settings': [[12, 'rows'], [8, 'cards'], [4, 'text']],
  '/profile': [[5, 'text'], [7, 'rows'], [7, 'rows'], [5, 'text']],
};

function BlockSk({ span, kind, i }: { span: number; kind: string; i: number }) {
  return (
    <section className="blk sk-blk" style={{ '--span': span, '--i': i } as CSSProperties}>
      <Sk w="38%" h={16} r={6} /><Sk w="22%" h={10} r={5} className="sk-sub" />
      <div className={cx('sk-in', kind)}>
        {/* charts in Web ORCA are lines (price, portfolio history): the placeholder is a faint line too */}
        {kind === 'chart' && <div className="sk-line"><svg viewBox="0 0 400 190" preserveAspectRatio="none" aria-hidden="true"><path d="M0 140 C40 132 60 150 96 128 S150 96 190 108 S250 70 290 82 S360 46 400 52" /></svg></div>}
        {kind === 'cards' && Array.from({ length: 3 }, (_, k) => <Sk key={k} h={150} r={18} />)}
        {kind === 'rows' && Array.from({ length: 4 }, (_, k) => <div className="sk-row" key={k}><Sk w="38px" h={38} r={19} /><div><Sk w="70%" h={12} r={5} /><Sk w="40%" h={9} r={5} /></div></div>)}
        {kind === 'text' && <><Sk h={12} r={5} /><Sk w="92%" h={12} r={5} /><Sk w="76%" h={12} r={5} /><Sk w="40%" h={36} r={18} className="sk-btn" /></>}
      </div>
    </section>
  );
}

/** Whole-page skeleton for a route (used as the Suspense fallback). */
export function RouteSkeleton({ path }: { path: string }) {
  const page = pageByPath(path);
  if (path === '/ai') {
    return (
      <div className="panel sk-panel ai" aria-busy="true" aria-label={i18n.t('common:action.loading')}>
        <div className="sk-top"><Sk w="40px" h={40} r={14} /><Sk w="90px" h={22} r={8} /><span /><Sk w="40px" h={40} r={14} /></div>
        <div className="sk-ai">
          <aside>{Array.from({ length: 6 }, (_, k) => <Sk key={k} h={44} r={14} />)}</aside>
          <div className="sk-ai-main"><Sk w="46%" h={34} r={10} /><span className="sk-ring" /><Sk w="70%" h={52} r={26} /></div>
        </div>
      </div>
    );
  }
  return (
    <div aria-busy="true" aria-label={`${i18n.t('common:action.loading')} ${page.title}`}>
      <div className="panel sk-panel">
        <div className="sk-top"><Sk w="40px" h={40} r={14} /><Sk w="90px" h={22} r={8} /><span className="sk-pills"><Sk w="320px" h={40} r={20} /></span><Sk w="160px" h={40} r={20} /></div>
        <div className="sk-hero">
          <div className="sk-l">
            <h1 className="h-title sk-title">{page.title}</h1>
            <div className="sk-tabs">{[70, 84, 96].map((w) => <Sk key={w} w={`${w}px`} h={32} r={16} />)}</div>
            <div className="sk-metric"><Sk w="38%" h={84} r={14} /><Sk w="34%" h={70} r={14} /></div>
            <div className="sk-minis"><Sk h={100} r={24} /><Sk h={100} r={24} /></div>
          </div>
          <div className="sk-r">{Array.from({ length: 6 }, (_, k) => <Sk key={k} h={150} r={24} />)}</div>
        </div>
      </div>
      <main className="lower">{(LOWER[path] ?? LOWER['/']).map(([span, kind], i) => <BlockSk key={i} span={span} kind={kind} i={i} />)}</main>
    </div>
  );
}
